import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { signInWithGitHub, signInWithGoogle } from '@/lib/auth-client';
import { Github, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function LoginModal({ isOpen, onClose }: LoginModalProps) {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState<'github' | 'google' | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleGitHubLogin = async () => {
        setIsLoading('github');
        setError(null);

        try {
            await signInWithGitHub();
            onClose();
        } catch (err) {
            setError(t('auth.errors.github_failed'));
            console.error('GitHub login failed:', err);
        } finally {
            setIsLoading(null);
        }
    };

    const handleGoogleLogin = async () => {
        setIsLoading('google');
        setError(null);

        try {
            await signInWithGoogle();
            onClose();
        } catch (err) {
            setError(t('auth.errors.google_failed'));
            console.error('Google login failed:', err);
        } finally {
            setIsLoading(null);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={t('auth.sign_in')}>
            <div className="space-y-6">
                <p className="text-sm text-gray-600 dark:text-gray-400">{t('auth.sign_in_description')}</p>

                {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">{error}</div>}

                <div className="space-y-3">
                    <Button
                        onClick={handleGitHubLogin}
                        disabled={isLoading !== null}
                        className="flex w-full items-center justify-center gap-2 bg-gray-900 text-white hover:bg-gray-800 dark:bg-gray-700 dark:hover:bg-gray-600"
                    >
                        {isLoading === 'github' ? <Loader2 className="h-5 w-5 animate-spin" /> : <Github className="h-5 w-5" />}
                        {t('auth.continue_with_github')}
                    </Button>

                    <Button
                        onClick={handleGoogleLogin}
                        disabled={isLoading !== null}
                        className="flex w-full items-center justify-center gap-2 border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                        {isLoading === 'google' ? <Loader2 className="h-5 w-5 animate-spin" /> : <GoogleIcon className="h-5 w-5" />}
                        {t('auth.continue_with_google')}
                    </Button>
                </div>

                <p className="text-center text-xs text-gray-500 dark:text-gray-400">{t('auth.terms_notice')}</p>
            </div>
        </Modal>
    );
}

function GoogleIcon({ className }: { className?: string }) {
    return (
        <svg className={className} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-label="Google">
            <title>Google</title>
            <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
            />
            <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
            />
            <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
            />
            <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
            />
        </svg>
    );
}
